import React from 'react'
import { CANVAS_ROTATE_CURSORS } from './cursors/canvasRotateCursors'
import type { ResizeCorner } from './utils/canvasAlignment'
import type { RotateDrag } from './hooks/useRotateDrag'
import './RotateHandles.scss'

/** Dieselben vier Ecken wie beim Skalieren — und in derselben Reihenfolge. */
const CORNERS: readonly ResizeCorner[] = ['top-left', 'top-right', 'bottom-left', 'bottom-right']

export interface RotateHandlesProps {
  /** Ob die Zonen gelten. Beim Zettel: einzeln ausgewählt, nicht im Bearbeiten-Modus, nicht im Hand-Werkzeug. */
  shown: boolean
  /** Die Pointer-Events aus `useRotateDrag` — der Node hält die Geste, hier wird sie nur angefasst. */
  handlers: RotateDrag['handlers']
}

/**
 * Die vier **Dreh-Zonen** — je eine schräg außerhalb einer Ecke.
 *
 * **Unsichtbar. Was sie ankündigt, ist der Cursor** (CONTEXT.md, „Neigung"):
 * ein gebogener Doppelpfeil, je Ecke ein anderer. Ein fünftes sichtbares
 * Bedienelement neben Ziehpunkt, Anlegepunkt, Tauschpunkt und Leiste hätte den
 * Zettel zugestellt.
 *
 * **Sie liegen im ungedrehten Kasten**, als Geschwister der Karte und nicht in
 * ihr: Die Karte kippt, die Zonen nicht. Wären sie ihre Kinder, wanderten sie
 * mit dem Papier davon — und ein Griff, der dem eigenen Zug hinterherläuft, ist
 * keiner.
 *
 * **Schräg NACH AUSSEN versetzt**, mit einem Abstand, der den 12px-Ziehpunkt
 * auf derselben Ecke frei lässt: Überlappten sie sich, entschiede die
 * Stapelreihenfolge, ob ein Zug dreht oder skaliert.
 *
 * Maße in **Bildschirm-Pixeln** (`--canvas-scale`, wie die Ziehpunkte) — bei
 * 30 % Zoom wären 22 Flächen-Pixel nicht mehr zu treffen.
 *
 * `nodrag` und `nopan`: React Flow soll aus einem Druck auf sie weder ein
 * Verschieben des Nodes noch ein Fahren der Ansicht machen.
 *
 * @example
 * const rotate = useRotateDrag({ getCenter, getRasten, getStart, onCommit, onReset })
 * <RotateHandles shown={showRotate} handlers={rotate.handlers} />
 */
export const RotateHandles: React.FC<RotateHandlesProps> = ({ shown, handlers }) => {
  if (!shown) return null

  return (
    <>
      {CORNERS.map(corner => (
        <div
          key={corner}
          className={`canvas-rotate-handle canvas-rotate-handle--${corner} nodrag nopan`}
          // Der Cursor als DATEN von hier, das Rezept in der SCSS — dieselbe
          // Aufteilung wie beim Bild des Klebestreifens.
          style={{ '--canvas-rotate-cursor': CANVAS_ROTATE_CURSORS[corner] } as React.CSSProperties}
          /*
            Aus dem Barrierefreiheits-Baum heraus: Die Zone hat keinen Namen und
            keinen Tastaturweg — die Pfeiltasten verschieben am ausgewählten
            Node, und zwei Bedeutungen auf einer Taste wären schlimmer als eine
            fehlende. Eine bewusste Asymmetrie zum **Klebestreifen**, der seine
            Pfeiltasten behält (er ist kein Node).
          */
          aria-hidden="true"
          {...handlers}
        />
      ))}
    </>
  )
}
